import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { useNavigate } from 'react-router-dom';
import axiosInstance, { API_BASE_URL } from 'config/axiosInstance';
import { Link } from "react-router-dom";

const LiveWebinarsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const itemsPerPage = 12;
  const [totalCount, setTotalCount] = useState(0);
  
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (showMobileDrawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileDrawer]);

  const fetchWebinars = async () => {
    try {
      setLoading(true);
      setError(null);
      setWebinars([]);
      setProgress(0);
      setTotalCount(0);

      let nextUrl = `webinars/?webinar_type=live&status=scheduled&page_size=50`;
      let totalCountLocal = 0;

      while (nextUrl) {
        const response = await axiosInstance.get(nextUrl);
        
        if (totalCountLocal === 0) {
          totalCountLocal = response.data.count || response.data.results?.length || 117;
          setTotalCount(totalCountLocal);
        }
        
        const webinarsPage = response.data.results 
          ? response.data.results.map(transformWebinarData)
          : response.data.map(transformWebinarData);
        
        setWebinars(prevWebinars => {
          const updatedWebinars = [...prevWebinars, ...webinarsPage];
          const sortedWebinars = updatedWebinars.sort((a, b) => 
            new Date(a.scheduled_date || a.date) - new Date(b.scheduled_date || b.date)
          );
          
          const newProgress = totalCountLocal > 0 
            ? Math.min(100, (sortedWebinars.length / totalCountLocal) * 100)
            : Math.min(100, (sortedWebinars.length / 117) * 100);
          
          setProgress(newProgress);
          
          return sortedWebinars;
        });
        
        nextUrl = response.data.next;
        
        if (nextUrl) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
    } catch (err) {
      console.error('Error fetching webinars:', err);
      setError('Failed to load webinars. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebinars();
  }, [API_BASE_URL]);

  const transformWebinarData = (apiWebinar) => {
    const utcDate = new Date(apiWebinar.scheduled_date);
    const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);

    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const month = months[istDate.getUTCMonth()];
    const day = istDate.getUTCDate();
    const year = istDate.getUTCFullYear();

    const format12h = (date) => {
      let hours = date.getUTCHours();
      const minutes = date.getUTCMinutes().toString().padStart(2,'0');
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${period}`;
    };

    const pstTime = format12h(istDate) + ' PST';
    const estTime = format12h(new Date(istDate.getTime() + 3*60*60*1000)) + ' EST';
    const timeString = `${pstTime} | ${estTime}`;

    return {
      id: apiWebinar.id,
      webinarId: apiWebinar.webinar_id,
      title: apiWebinar.title,
      date: day.toString(),
      month: month.toUpperCase(),
      instructor: {
        name: apiWebinar.speaker?.full_name || apiWebinar.speaker?.name || 'Instructor',
        avatar: apiWebinar.speaker?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&auto=format&q=60'
      },
      image: apiWebinar.cover_image_url || 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=500&h=300&fit=crop&auto=format&q=80',
      rating: apiWebinar.analytics?.average_rating || 4.8,
      reviewCount: apiWebinar.analytics?.total_reviews
        ? `${(apiWebinar.analytics.total_reviews / 1000).toFixed(1)}k`
        : '0',
      price: `$${parseFloat(apiWebinar.main_price).toFixed(2)}`,
      originalPrice: apiWebinar.original_price
        ? `$${parseFloat(apiWebinar.original_price).toFixed(2)}`
        : null,
      duration: apiWebinar.duration || 'N/A',
      level: apiWebinar.skill_level || 'All Levels',
      students: apiWebinar.total_students
        ? `${(apiWebinar.total_students / 1000).toFixed(1)}k`
        : '0',
      category: apiWebinar.category?.name || 'General',
      fullDate: `${month} ${day}, ${year}`,
      timeRange: timeString,
      scheduled_date: apiWebinar.scheduled_date
    };
  };

  const filterOptions = useMemo(() => {
    const months = [...new Set(webinars.map(w => new Date(w.scheduled_date).toLocaleString('default', { month: 'long' })))];
    const instructors = [...new Set(webinars.map(w => w.instructor.name))];
    const categories = [...new Set(webinars.map(w => w.category))];
    return { months, instructors, categories };
  }, [webinars]);

  const handleMonthToggle = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  const handleInstructorToggle = (instructor) => {
    setSelectedInstructors(prev => 
      prev.includes(instructor) ? prev.filter(i => i !== instructor) : [...prev, instructor]
    );
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const filteredWebinars = useMemo(() => {
    let filtered = webinars.filter(webinar => {
      const matchesSearch = searchQuery === '' || 
        webinar.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        webinar.instructor.name.toLowerCase().includes(searchQuery.toLowerCase());

      const webinarMonth = new Date(webinar.scheduled_date).toLocaleString('default', { month: 'long' });
      const matchesMonth = selectedMonths.length === 0 || selectedMonths.includes(webinarMonth);
      
      const matchesInstructor = selectedInstructors.length === 0 || selectedInstructors.includes(webinar.instructor.name);
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(webinar.category);

      return matchesSearch && matchesMonth && matchesInstructor && matchesCategory;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.scheduled_date) - new Date(b.scheduled_date);
        case 'price-low':
          return parseFloat(a.price.slice(1)) - parseFloat(b.price.slice(1));
        case 'price-high':
          return parseFloat(b.price.slice(1)) - parseFloat(a.price.slice(1));
        default:
          return 0;
      }
    });

    return filtered;
  }, [webinars, searchQuery, selectedMonths, selectedInstructors, selectedCategories, sortBy]);

  const totalPages = Math.ceil(filteredWebinars.length / itemsPerPage);
  const paginatedWebinars = filteredWebinars.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedMonths, selectedInstructors, selectedCategories]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };
  
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedMonths([]);
    setSelectedInstructors([]);
    setSelectedCategories([]);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || selectedMonths.length > 0 || selectedInstructors.length > 0 || selectedCategories.length > 0;
  const activeFilterCount = [searchQuery, ...selectedMonths, ...selectedInstructors, ...selectedCategories].filter(Boolean).length;

  const handleApplyFilters = () => {
    setShowMobileDrawer(false);
  };

  const FilterSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Month Filter */}
      <div>
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <Icon name="Calendar" size={16} className="mr-2 text-emerald-600" />
          Month ({selectedMonths.length})
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {filterOptions.months.map((month) => (
            <label
              key={month}
              className="flex items-center p-2 rounded-lg hover:bg-white cursor-pointer transition-colors group"
            >
              <input
                type="checkbox"
                checked={selectedMonths.includes(month)}
                onChange={() => handleMonthToggle(month)}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <span className="ml-3 text-sm text-gray-700 group-hover:text-emerald-700 transition-colors">
                {month}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Instructor Filter */}
      <div>
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <Icon name="Users" size={16} className="mr-2 text-green-600" />
          Instructor ({selectedInstructors.length})
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {filterOptions.instructors.map((instructor) => (
            <label
              key={instructor}
              className="flex items-center p-2 rounded-lg hover:bg-white cursor-pointer transition-colors group"
            >
              <input
                type="checkbox"
                checked={selectedInstructors.includes(instructor)}
                onChange={() => handleInstructorToggle(instructor)}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="ml-3 text-sm text-gray-700 group-hover:text-green-700 transition-colors">
                {instructor}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div>
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <Icon name="Folder" size={16} className="mr-2 text-teal-600" />
          Category ({selectedCategories.length})
        </h4>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {filterOptions.categories.map((category) => (
            <label
              key={category}
              className="flex items-center p-2 rounded-lg hover:bg-white cursor-pointer transition-colors group"
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(category)}
                onChange={() => handleCategoryToggle(category)}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-3 text-sm text-gray-700 group-hover:text-teal-700 transition-colors">
                {category}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading && webinars.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50/20 to-teal-50/20">
        <main className="pt-14 md:pt-16 lg:pt-20">
          <div className="bg-gradient-to-r from-emerald-900 via-green-900 to-teal-900 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h1 className="text-4xl font-bold">Live Compliance Training</h1>
              <p className="text-xl text-emerald-100 mt-2">Loading webinars...</p>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex flex-col items-center justify-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full"
              />
              <p className="mt-4 text-gray-600">Loading webinar sessions...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50/20 to-teal-50/20">
        <main className="pt-14 md:pt-16 lg:pt-20">
          <div className="bg-gradient-to-r from-emerald-900 via-green-900 to-teal-900 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h1 className="text-4xl font-bold">Live Compliance Training</h1>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Webinars</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button 
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
              >
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50/20 to-teal-50/20">
      <main className="pt-14 md:pt-16 lg:pt-20">
        {/* Hero Header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-emerald-900 via-green-900 to-teal-900 text-white">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-10">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute bg-gradient-to-r from-emerald-500 to-green-500 rounded-full blur-3xl"
                style={{
                  width: `${Math.random() * 300 + 100}px`,
                  height: `${Math.random() * 300 + 100}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  x: [0, Math.random() * 200 - 100],
                  y: [0, Math.random() * 200 - 100],
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <motion.span
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-sm font-semibold mb-6"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.7, 1]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-2 h-2 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full mr-2"
                />
                Live Sessions
              </motion.span>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Interactive{' '}
                <span className="bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400 bg-clip-text text-transparent">
                  Live Webinars
                </span>
              </h1>
              
              <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
                {filteredWebinars.length} real-time compliance training sessions available
              </p>
            </motion.div>
          </div>
        </div>

        {/* Progress Bar */}
        {loading && webinars.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
              <motion.div
                className="bg-gradient-to-r from-emerald-500 to-green-500 h-2 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {Math.round(progress)}% loaded ({webinars.length}/{totalCount} webinars)
            </p>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Advanced Top Filters */}
          <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden border border-emerald-100">
            {/* Search and Filter Toggle */}
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative group">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-emerald-500 transition-colors" />
                    <input
                      type="text"
                      placeholder="Search webinars by title or instructor..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300 hover:border-emerald-300"
                    />
                  </div>
                </div>

                {/* Filter Toggle Button */}
                <motion.button
                  onClick={() => isMobile ? setShowMobileDrawer(true) : setShowFilters(!showFilters)}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl hover:from-emerald-600 hover:to-green-600 transition-all duration-300 font-medium shadow-md hover:shadow-lg group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Icon name="Filter" size={18} />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-white text-emerald-600 px-2 py-0.5 rounded-full text-xs font-bold"
                    >
                      {activeFilterCount}
                    </motion.span>
                  )}
                  <Icon name={showFilters ? "ChevronUp" : "ChevronDown"} size={16} className="hidden md:block transition-transform group-hover:translate-y-0.5" />
                </motion.button>

                {/* Sort */}
                <div className="relative group">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full md:w-auto px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none bg-white pr-10 hover:border-emerald-300 transition-colors"
                  >
                    <option value="date">Sort by Date</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                  <Icon name="ChevronDown" size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Desktop Expandable Filter Section */}
            <AnimatePresence>
              {showFilters && !isMobile && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-gray-200 overflow-hidden"
                >
                  <div className="p-4 md:p-6 bg-gradient-to-r from-emerald-50/50 to-green-50/50">
                    <FilterSection />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active Filters */}
            {hasActiveFilters && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-4 md:px-6 py-4 bg-gradient-to-r from-emerald-50 to-green-50 border-t border-emerald-200"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                  
                  {searchQuery && (
                    <motion.span
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery('')} className="hover:text-emerald-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  )}
                  
                  {selectedMonths.map(month => (
                    <motion.span
                      key={month}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      📅 {month}
                      <button onClick={() => handleMonthToggle(month)} className="hover:text-green-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  ))}
                  
                  {selectedInstructors.map(instructor => (
                    <motion.span
                      key={instructor}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      👤 {instructor}
                      <button onClick={() => handleInstructorToggle(instructor)} className="hover:text-teal-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  ))}
                  
                  {selectedCategories.map(category => (
                    <motion.span
                      key={category}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      📂 {category}
                      <button onClick={() => handleCategoryToggle(category)} className="hover:text-emerald-900">
                        <Icon name="X" size={12} />
                      </button>
                    </motion.span>
                  ))}
                  
                  <motion.button
                    onClick={clearFilters}
                    className="ml-auto text-sm text-gray-600 hover:text-gray-900 font-medium underline"
                    whileHover={{ scale: 1.05 }}
                  >
                    Clear All
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Mobile Filter Drawer */}
          <AnimatePresence>
            {showMobileDrawer && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                  onClick={() => setShowMobileDrawer(false)}
                />
                
                {/* Drawer */}
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white z-50 md:hidden shadow-2xl flex flex-col"
                >
                  {/* Drawer Header */}
                  <div className="flex items-center justify-between p-4 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50">
                    <h3 className="font-bold text-lg text-gray-900 flex items-center">
                      <Icon name="Filter" size={20} className="mr-2 text-emerald-600" />
                      Filters
                    </h3>
                    <button
                      onClick={() => setShowMobileDrawer(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <Icon name="X" size={20} className="text-gray-500" />
                    </button>
                  </div>
                  
                  {/* Drawer Content */}
                  <div className="flex-1 overflow-y-auto p-4">
                    <FilterSection />
                  </div>

                  {/* Drawer Footer */}
                  <div className="p-4 border-t border-emerald-200 bg-white space-y-2">
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="w-full py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Clear All Filters
                      </button>
                    )}
                    <button
                      onClick={handleApplyFilters}
                      className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold rounded-lg hover:from-emerald-600 hover:to-green-600 transition-all shadow-lg"
                    >
                      Apply Filters ({activeFilterCount})
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Results Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {filteredWebinars.length} Live Session{filteredWebinars.length !== 1 ? 's' : ''}
              </h2>
              <p className="text-gray-600 text-sm mt-1">Real-time compliance training</p>
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-sm text-emerald-600 hover:text-emerald-700"
                iconName="RefreshCw"
              >
                Reset Filters
              </Button>
            )}
          </motion.div>

          {/* Webinars Grid */}
          {paginatedWebinars.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 bg-white rounded-2xl shadow-lg"
            >
              <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No webinars found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
              <Button 
                onClick={clearFilters}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
              >
                Clear All Filters
              </Button>
            </motion.div>
          ) : (
            <>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
                layout
              >
                {paginatedWebinars.map((webinar, index) => {
                  const slug = generateSlug(webinar.title);
                  const webinarUrl = `/live-webinar/${webinar.webinarId}/${slug}`;
                  
                  return (
                    <motion.div
                      key={webinar.id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                      whileHover={{ 
                        y: -10,
                        boxShadow: "0 20px 40px rgba(16, 185, 129, 0.15)"
                      }}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-emerald-100 hover:border-emerald-300"
                    > 
                      <Link to={webinarUrl} className="block cursor-pointer">
                        {/* Image */}
                        <div className="relative overflow-hidden h-48">
                          <motion.img
                            src={webinar.image}
                            alt={webinar.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                            whileHover={{ scale: 1.1 }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          
                          {/* LIVE Badge */}
                          <motion.div
                            animate={{ 
                              scale: [1, 1.1, 1],
                              boxShadow: [
                                '0 0 0px rgba(239, 68, 68, 0)',
                                '0 0 20px rgba(239, 68, 68, 0.6)',
                                '0 0 0px rgba(239, 68, 68, 0)'
                              ]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute top-3 left-3"
                          >
                            <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              LIVE
                            </div>
                          </motion.div>

                          {/* Date Badge */}
                          <motion.div
                            whileHover={{ rotate: 5 }}
                            className="absolute top-3 right-3 bg-white rounded-xl p-3 shadow-2xl border border-emerald-200"
                          >
                            <div className="text-2xl font-black text-transparent bg-gradient-to-br from-emerald-600 to-green-600 bg-clip-text leading-none">
                              {webinar.date}
                            </div>
                            <div className="text-xs font-bold text-emerald-700 mt-0.5">
                              {webinar.month}
                            </div>
                          </motion.div>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 text-base leading-tight group-hover:text-emerald-700 transition-colors min-h-[3rem]">
                            {webinar.title}
                          </h3>

                          {/* Instructor & Rating */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                                <img
                                  src={webinar.instructor.avatar}
                                  alt={webinar.instructor.name}
                                  className="w-8 h-8 rounded-full object-cover border-2 border-emerald-200"
                                  loading="lazy"
                                />
                              </motion.div>
                              <span className="text-sm text-gray-700 truncate font-medium">
                                {webinar.instructor.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 ml-2 bg-emerald-50 px-2 py-1 rounded-lg">
                              <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
                              <span className="text-sm font-bold text-gray-900">{webinar.rating.toFixed(1)}</span>
                            </div>
                          </div>

                          {/* Meta Info */}
                          <div className="flex items-center justify-between text-sm text-gray-600 mb-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-3 border border-emerald-100">
                            <div className="flex items-center gap-2">
                              <Icon name="Clock" size={14} className="text-emerald-600" />
                              <span className="font-medium">{webinar.duration} min</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon name="Users" size={14} className="text-green-600" />
                              <span className="font-medium">{webinar.students} enrolled</span>
                            </div>
                          </div>

                          {/* Session Info */}
                          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 mb-4 border border-emerald-200">
                            <div className="text-sm font-semibold text-emerald-800 mb-1 flex items-center gap-2">
                              <Icon name="Calendar" size={14} />
                              {webinar.fullDate}
                            </div>
                            <div className="text-xs text-emerald-600 flex items-center gap-2">
                              <Icon name="Clock" size={12} />
                              {webinar.timeRange}
                            </div>
                          </div>

                          {/* Pricing & CTA */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-black text-transparent bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text">
                                {webinar.price}
                              </span>
                              {webinar.originalPrice && (
                                <span className="text-sm text-gray-400 line-through">{webinar.originalPrice}</span>
                              )}
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="relative overflow-hidden"
                            >
                              <Link
                                to={webinarUrl}
                                onClick={(e) => e.stopPropagation()}
                                className="relative z-10 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold py-2.5 px-5 rounded-xl text-sm hover:shadow-xl hover:shadow-emerald-500/30 transition-all flex items-center gap-2 group/btn"
                              >
                                Join Session
                                <Icon name="ArrowRight" size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                              </Link>
                              <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-700 rounded-xl"
                                initial={{ x: "-100%" }}
                                whileHover={{ x: 0 }}
                                transition={{ duration: 0.3 }}
                              />
                            </motion.button>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center items-center space-x-2 mt-12 pt-8"
                >
                  <Button
                    variant="outline"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="text-gray-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300"
                    iconName="ChevronLeft"
                    iconPosition="left"
                  >
                    Previous
                  </Button>
                  
                  <div className="flex space-x-2">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let page = i + 1;
                      if (totalPages > 7) {
                        if (currentPage <= 4) page = i + 1;
                        else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                        else page = currentPage - 3 + i;
                      }
                      return (
                        <motion.button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                            currentPage === page
                              ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg'
                              : 'bg-white border border-emerald-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300'
                          }`}
                        >
                          {page}
                        </motion.button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="text-gray-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300"
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Next
                  </Button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default LiveWebinarsPage;